import { type PropsWithChildren } from 'react'
import { spacing, useIsMobileVariant } from '../../theme'
import { Box } from '../../primitives/Box'
import { IconSeverity, type IconSeverityProps } from '../../primitives/IconSeverity'

export type ListItemIconProps = PropsWithChildren<{
  /**
   * When you want the element to be part of your setup, for example a Checkbox for its functionality
   * but not showing it visually. In most other cases you will not render the `ListItemIcon` at all.
   */
  isHidden?: boolean

  /**
   * You can turn off/on the mechanism the icon is automatically supported with a block arround it depending on its theme variant.
   * When leaving the prop out with variant mobile, it will not show the block and on any other it will be shown.
   *
   * If your component (not) needs the default behaviour, you can use this prop otherwise please leave it out :)
   */
  isBlock?: boolean

  /**
   * Icons can be used with a severity
   */
  severity?: IconSeverityProps['severity']
}>

export const ListItemIcon = ({ children, isBlock, isHidden, severity }: ListItemIconProps) => {
  const isMobile = useIsMobileVariant()
  const showBlock = isBlock === undefined ? !isMobile : isBlock
  const dimension = showBlock ? spacing(5) : spacing(3)

  return (
    <Box
      className="DhlListItem-icon"
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        color: 'inherit',
        boxSizing: 'content-box',
        lineHeight: 0,
        // hidden from UI perspective, still needs to be present in the DOM else user should not use this element at all :)
        ...(isHidden
          ? {
              width: 0,
              height: 0,
              padding: 0,
              minWidth: 0,
            }
          : { width: dimension, minWidth: dimension, height: dimension, pr: spacing(1.75) }),
      }}
    >
      <IconSeverity isBlock={showBlock} severity={severity}>
        {children}
      </IconSeverity>
    </Box>
  )
}
